import { useMemo } from 'react';
import type { AiSuggestion } from '../../components/AiSuggestionsFab.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useEvents, useMasters, useStockProjection } from '../data/queries.js';
import { type TempReadingLike } from './overdue.js';
import {
  computeSuggestions,
  type CleaningEventRow,
  type CleaningScheduleRow,
  type FoodRow,
  type ProbeAlertRow,
  type StorageUnitRow,
} from './suggestions-core.js';
import { useOperationalIdentity } from '../auth/useOperationalIdentity.js';
import { isAssignedToOperationalIdentity } from '../auth/operationalAssignment.js';
import { useExpirationPreferences } from './expirationPreferences.js';

interface BusinessClosed {
  id: string;
  giorni_chiusura?: string[] | null;
}

export { computeSuggestions } from './suggestions-core.js';
export type { SuggestionsInput } from './suggestions-core.js';

/**
 * Builds the contextual AI-suggestions list shown in the FAB across pages.
 * Centralised so every page that mounts <AiSuggestionsFab/> shares the same
 * priorities (alerts → overdue cleanings → expiring lots → missing temps).
 */
export function useAiSuggestions(): AiSuggestion[] {
  const { profile } = useAuth();
  const identity = useOperationalIdentity();
  const expirationPreferences = useExpirationPreferences(profile?.active_business_id ?? null);
  const { data: units = [] } = useMasters<StorageUnitRow>('storage_units');
  const { data: schedules = [] } = useMasters<CleaningScheduleRow>('cleaning_schedules');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { data: businesses = [] } = useMasters<BusinessClosed>('businesses');
  const { data: tempReads = [] } = useEvents('temperature_reading', 500);
  const { data: cleanEvents = [] } = useEvents('cleaning_done', 500);
  const { data: alerts = [] } = useEvents('probe_alert', 20);
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const stockProjection = useStockProjection({ canonicalUomByFood });

  const giorniChiusura = useMemo(() => {
    const biz = businesses.find((b) => b.id === profile?.active_business_id);
    return biz?.giorni_chiusura ?? [];
  }, [businesses, profile?.active_business_id]);

  return useMemo<AiSuggestion[]>(
    () =>
      computeSuggestions({
        units: units.filter((unit) => isAssignedToOperationalIdentity(
          unit.assigned_personnel_ids,
          identity.personnelId,
          identity.canManageOperationalWork,
        )),
        schedules: schedules.filter((schedule) => isAssignedToOperationalIdentity(
          schedule.assigned_personnel_ids,
          identity.personnelId,
          identity.canManageOperationalWork,
        )),
        foods,
        tempReads: tempReads as (TempReadingLike & { client_ts: string })[],
        cleanEvents: cleanEvents as CleaningEventRow[],
        alerts: alerts as ProbeAlertRow[],
        stockBalances: stockProjection.data.balances,
        now: Date.now(),
        expirationLeadHours: expirationPreferences.alertLeadHours,
        giorni_chiusura: giorniChiusura,
      }),
    [
      units,
      schedules,
      foods,
      tempReads,
      cleanEvents,
      alerts,
      stockProjection.data.balances,
      giorniChiusura,
      identity.canManageOperationalWork,
      identity.personnelId,
      expirationPreferences.alertLeadHours,
    ],
  );
}
